import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart as RechartsLineChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { CustomTooltip } from './CustomTooltip';
import { CHART_COLORS, CHART_CONFIG } from './styles';
import './styles.css';
import { type LineChartProps, type LineChartDataPoint } from './types';
import { calculateDefaultYDomain } from './utils';

const DEFAULT_HEIGHT = 360;

const axisProps = {
  axisLine: { stroke: CHART_COLORS.axis, strokeWidth: CHART_CONFIG.strokeWidth.axis },
  tickLine: { stroke: CHART_COLORS.axis, strokeWidth: CHART_CONFIG.strokeWidth.axis },
};

const tickStyle = {
  fontSize: CHART_CONFIG.fontSize.axis,
  fill: CHART_COLORS.text,
};

export const LineChart = <T extends LineChartDataPoint>({
  data,
  categories,
  xAxisKey,
  height = DEFAULT_HEIGHT,
  title,
  isLoading = false,
  error = null,
  formatXAxisLabel,
  formatYAxisLabel,
  getCategoryLabel,
  formatTooltipLabel,
  calculateYDomain,
  noDataMessage,
  loadingMessage,
}: LineChartProps<T>): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);

  if (isLoading) {
    return loadingMessage ? (
      <div className="flex flex-col items-center justify-center h-64 gap-2">
        <p className="text-gray-600 text-center">{loadingMessage}</p>
      </div>
    ) : (
      <LoadingScreen />
    );
  }

  const safeData = data ?? [];

  if (error || !safeData.length) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2">
        <p className="text-gray-600 text-center">
          {noDataMessage ?? t('DailyTracking.Chart.empty')}
        </p>
      </div>
    );
  }

  const { yDomain, yTicks } = calculateYDomain
    ? calculateYDomain(safeData)
    : calculateDefaultYDomain(safeData, categories);

  const categoryKeys = categories.map(cat => cat.key);

  return (
    <div className="w-full overflow-scroll no-scrollbar">
      {title && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-center sm:text-left text-gray-800">{title}</h3>
        </div>
      )}
      <div
        className="w-full min-w-[300px]"
        style={{
          height: `${height}px`,
          minHeight: `${height}px`,
        }}
      >
        <ResponsiveContainer width="100%" height={height} minHeight={height} minWidth={300}>
          <RechartsLineChart data={safeData} margin={CHART_CONFIG.margin} accessibilityLayer>
            <CartesianGrid
              strokeDasharray={CHART_CONFIG.gridDashArray}
              stroke={CHART_COLORS.grid}
              horizontal
              vertical
            />
            <XAxis
              dataKey={xAxisKey}
              {...axisProps}
              tick={{ ...tickStyle, textAnchor: 'middle' }}
              tickMargin={10}
              tickFormatter={formatXAxisLabel}
              angle={0}
              dy={10}
            />
            <YAxis
              domain={yDomain}
              ticks={yTicks}
              {...axisProps}
              tick={{
                ...tickStyle,
                textAnchor: 'end',
                dominantBaseline: 'middle',
              }}
              tickFormatter={formatYAxisLabel}
              tickMargin={CHART_CONFIG.tickMargin}
              width={CHART_CONFIG.yAxisWidth}
            />
            <Tooltip
              content={
                <CustomTooltip
                  categories={categoryKeys}
                  getCategoryLabel={getCategoryLabel}
                  formatLabel={formatTooltipLabel}
                />
              }
              cursor={{
                stroke: CHART_COLORS.hover,
                strokeWidth: CHART_CONFIG.strokeWidth.hover,
                strokeDasharray: CHART_CONFIG.hoverDashArray,
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
              formatter={(value: string) => {
                const category = categories.find(cat => cat.key === value);
                return category?.label ?? value;
              }}
            />
            {categories.map(category => (
              <Line
                key={category.key}
                type="monotone"
                dataKey={category.key}
                name={category.key}
                stroke={category.color}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
